import { useState, useEffect } from "react";
import { Building, Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Establishment {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  city: string | null;
}

export function EstablishmentSelector() {
  const { isSuperAdmin, overrideEstablishmentId, setOverrideEstablishmentId } = useAuth();
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch all establishments
  useEffect(() => {
    if (isSuperAdmin) {
      setLoading(true);
      supabase
        .from("establishments")
        .select("id, name, slug, is_active, city")
        .order("name")
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching establishments:", error);
          }
          setEstablishments(data || []);
          setLoading(false);
        });
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  const selectedEstablishment = establishments.find(
    (e) => e.id === overrideEstablishmentId
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between min-w-0 w-full max-w-[200px] md:max-w-[300px]"
        >
          <div className="flex items-center gap-2 truncate">
            <Building className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate hidden sm:inline">
              {selectedEstablishment?.name || "Établissement"}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher un établissement..." />
          <CommandList>
            <CommandEmpty>
              {loading ? "Chargement..." : "Aucun établissement trouvé."}
            </CommandEmpty>
            <CommandGroup heading="Établissements">
              {/* Option pour réinitialiser */}
              {overrideEstablishmentId && (
                <CommandItem
                  onSelect={() => {
                    setOverrideEstablishmentId(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  <span>Quitter ce mode</span>
                </CommandItem>
              )}
              {establishments.map((establishment) => (
                <CommandItem
                  key={establishment.id}
                  value={`${establishment.name} ${establishment.city || ""}`}
                  onSelect={() => {
                    setOverrideEstablishmentId(establishment.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      overrideEstablishmentId === establishment.id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">{establishment.name}</span>
                    {establishment.city && (
                      <span className="text-xs text-muted-foreground truncate">
                        {establishment.city}
                      </span>
                    )}
                  </div>
                  {!establishment.is_active && (
                    <Badge variant="secondary" className="ml-2 shrink-0">
                      Inactif
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
